import React, { useMemo, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  ExternalLink,
  Headphones,
  Play,
  RotateCcw,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const romanBritainEpisodes = [
  {
    title: 'Episode 1: The Living Past: Crafting Historical Fiction',
    eyebrow: 'Behind the scenes',
    description:
      'A reflective introduction to the craft behind Blood and Honour and the wider Roman Storyworld: landscape, research, memory, and the act of turning history into living fiction.',
    image: '/images/links/Episode 1 The Living Past Crafting Historical Fiction.webp',
    link: 'https://creators.spotify.com/pod/profile/antro998/episodes/The-Living-Past-Crafting-Historical-Fiction-e370n1j',
  },
  {
    title: 'Episode 2: Varro — Blood, Honour, and Rebellion',
    eyebrow: 'Fallen officer',
    description:
      'Begin with Varro: a Roman officer broken by civil war, stripped of honour, and forced to choose whether survival is enough when justice still calls from the road ahead.',
    image: '/images/links/Episode 2 Varro Blood Honour Rebellion.webp',
    link: 'https://open.spotify.com/episode/51bvZQfXnvOD37BAERFWLH?si=ed8ca5cd52e54582',
  },
  {
    title: 'Episode 3: Arena Redemption — The Champion’s Sacrifice',
    eyebrow: 'Arena and mercy',
    description:
      'A story of blood, spectacle, and unexpected brotherhood, where the arena becomes more than a place of death: it becomes the threshold of redemption.',
    image: '/images/links/Episode 3 Arena Redemption The Champions Sacrifice.webp',
    link: 'https://open.spotify.com/episode/2GHnmTTmYtEpi9cdaMNPsY?si=f26551ec16794448',
  },
  {
    title: 'Episode 4: The Rise and Reckoning of Paulus Catena',
    eyebrow: 'Power corrupted',
    description:
      'Step into the shadow cast by Paulus Catena: ambition, cruelty, profit, and the terrible machinery of Roman authority turned against the vulnerable.',
    image: '/images/links/Episode 4 The Rise and Reckoning of Paulus Catena.webp',
    link: 'https://open.spotify.com/episode/28MPEYN2FsHtQ0gAD28yIX?si=5be3184bf2334f7c',
  },
  {
    title: 'Episode 5: Nestor — Physician, Rebel, and Quiet Hero',
    eyebrow: 'The healer',
    description:
      'Nestor stands between oath and conscience, medicine and rebellion, healing and helplessness. His courage is quieter than a sword, but no less vital.',
    image: '/images/links/Episode 5 Nestor Physician Rebel and Quiet Hero.webp',
    link: 'https://open.spotify.com/episode/5zbq68Kzuhw43JiG41vQfc?si=241fa0a8d9b8424b',
  },
  {
    title: 'Episode 6: Lucius Cunovalos — A Steadfast Shield of Honour',
    eyebrow: 'Between two worlds',
    description:
      'Lucius carries Roman discipline and Cornovii blood, standing as shield, witness, and bridge between peoples who must either unite or be broken apart.',
    image: '/images/links/Episode 6 Lucius Cunovalos A Steadfast Shield of Honour.webp',
    link: 'https://open.spotify.com/episode/0Phu9phzE46rETAxzDvl5f?si=c9d7a21070114542',
  },
  {
    title: 'Episode 7: Julius Sabinianus — Loyalty Forged in Blood and Bond',
    eyebrow: 'Duty and sacrifice',
    description:
      'Julius embodies loyalty under pressure: a man of iron whose choices remind the living what duty costs when the road narrows and others must escape.',
    image: '/images/links/Episode 7 Julius Sabinianus Loyalty Forged in Blood and Bond.webp',
    link: 'https://open.spotify.com/episode/62wHvmSgROXmI4XQjipYbR?si=143cb0e156cf4645',
  },
  {
    title: 'Episode 8: Aerwyna — Daughter of the Ordovices Hills',
    eyebrow: 'Hill-born authority',
    description:
      'Aerwyna emerges from grief, land, and tribal memory: not merely a companion to war, but a leader shaped by the hills and answerable to her people.',
    image: '/images/links/Episode 8 Aerwyna Daughter of the Ordovices Hills.webp',
    link: 'https://open.spotify.com/episode/4NfCujuJ3K2ODU8FSKgkpb?si=e93a51235a28428d',
  },
  {
    title: 'Episode 9: Lucius and Aerwyna — A Unity of Souls',
    eyebrow: 'Bond and alliance',
    description:
      'More than romance, this episode explores a union of cultures, wounds, loyalties, and futures in a Britain where love itself becomes political.',
    image: '/images/links/Episode 9 Lucius and Aerwyna A Unity of Souls.webp',
    link: 'https://open.spotify.com/episode/6nOSQ9K7Qyrnr00ahXLZKP?si=40be1388bbff4b3a',
  },
  {
    title: 'Episode 10: Cunobara — The Spirit of the Hills',
    eyebrow: 'Old strength',
    description:
      'Cunobara carries the authority of age, memory, and place. Her presence roots the story in the older powers of hill, hearth, kinship, and endurance.',
    image: '/images/links/Episode 10 Cunobara The Spirit of the Hills.webp',
    link: 'https://open.spotify.com/episode/6JpCmxNWcRQv7sJehnFd94?si=etYRePMXTiy7fDD9bEqZiQ',
  },
  {
    title: 'Episode 11: Shadows of the Eagle',
    eyebrow: 'The struggle deepens',
    description:
      'Enter the second book of the Sons of the Standard trilogy, where fractured loyalties, tribal resistance, sacrifice, and the shadow of Roman power draw Varro and his companions into a wider struggle for Britannia.',
    image: '/images/links/weight-of-wings.webp',
    link: 'https://spotifycreators-web.app.link/e/qA4zNYjKf5b',
  },
  {
    title: 'Episode 12: Sons of the Standard',
    eyebrow: 'The trilogy concludes',
    description:
      'The final book brings the surviving warriors, tribes, and families to their reckoning as Varro must decide what remains of Rome within him—and what future waits beyond Britannia.',
    image: '/images/links/04-rearguard-refused.webp',
    link: 'https://spotifycreators-web.app.link/e/3rcOgLwKf5b',
  },
];

const socialMediaLinks = [
  {
    title: 'Follow on X',
    platform: '',
    description:
      'Follow updates from Martin Antrobus: Roman Britain, field walks, research discoveries, book news, and the continuing growth of Roman Storyworld.',
    image: '/images/links/X.webp',
    url: 'https://x.com/antro_m197',
  },
  {
    title: 'Join on Truth Social',
    platform: '',
    description:
      'Connect with Roman Storyworld through additional updates, reflections, historical notes, and behind-the-scenes material from the books and research.',
    image: '/images/links/Truth Social.webp',
    url: 'https://truthsocial.com/@antro_998',
  },
];

function Links() {
  const [episodeIndex, setEpisodeIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);

  const currentEpisode = romanBritainEpisodes[episodeIndex];
  const progress = `${episodeIndex + 1} / ${romanBritainEpisodes.length}`;

  const seriesThemes = useMemo(
    () => [
      'Blood and Honour',
      'Shadows of the Eagle',
      'Sons of the Standard',
      'Character journeys',
      'Roman Britain',
    ],
    []
  );

  function goPreviousEpisode() {
    setFlipped(false);
    setEpisodeIndex((value) =>
      value === 0 ? romanBritainEpisodes.length - 1 : value - 1
    );
  }

  function goNextEpisode() {
    setFlipped(false);
    setEpisodeIndex((value) =>
      value === romanBritainEpisodes.length - 1 ? 0 : value + 1
    );
  }

  return (
    <main className="min-h-screen bg-stone-950 text-stone-100 overflow-hidden">
      <section className="relative min-h-screen py-10 px-4 sm:px-6 lg:px-8">
        <div
          className="absolute inset-0 bg-cover bg-center transition-all duration-700 scale-105 opacity-45"
          style={{ backgroundImage: `url(${currentEpisode.image})` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-stone-950/80 to-black/95" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(245,158,11,0.16),transparent_42%)]" />

        <div className="relative max-w-6xl mx-auto">
          <header className="text-center mb-10">
            <p className="uppercase tracking-[0.35em] text-amber-300 text-xs mb-3">
              Roman Storyworld Media
            </p>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
              Podcasts & Social Media
            </h1>
            <p className="max-w-3xl mx-auto text-stone-200 text-base md:text-lg">
              Listen to the voices, places, characters, and behind-the-scenes journeys that shape the wider Roman Storyworld.
            </p>
          </header>

          <section className="mb-16" aria-label="Roman Britain Unveiled podcast carousel">
            <div className="flex items-center justify-center mb-8">
              <Headphones className="h-7 w-7 text-green-400 mr-3" />
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                Roman Britain Unveiled
              </h2>
            </div>

            <div className="relative flex items-center justify-center gap-4">
              <button
                type="button"
                onClick={goPreviousEpisode}
                className="hidden md:flex h-12 w-12 rounded-full border border-white/30 bg-black/40 hover:bg-amber-500 hover:text-black transition items-center justify-center"
                aria-label="Previous episode"
              >
                <ArrowLeft size={22} />
              </button>

              <article className="w-full max-w-4xl rounded-3xl border border-white/15 bg-white/10 backdrop-blur-md shadow-2xl overflow-hidden">
                <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] min-h-[520px]">
                  <div className="relative min-h-[300px] lg:min-h-full overflow-hidden">
                    <img
                      src={currentEpisode.image}
                      alt=""
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    <div className="absolute left-5 right-5 bottom-5">
                      <p className="text-xs uppercase tracking-[0.25em] text-amber-200 mb-2">
                        {currentEpisode.eyebrow}
                      </p>
                      <p className="text-sm text-stone-200">Episode {progress}</p>
                    </div>
                  </div>

                  <div className="p-6 md:p-8 flex flex-col">
                    <div className="flex items-center justify-between gap-3 mb-5">
                      <span className="rounded-full bg-green-500/15 text-green-200 border border-green-400/30 px-3 py-1 text-xs font-semibold">
                        {romanBritainEpisodes.length} audio features
                      </span>
                      <button
                        type="button"
                        onClick={() => setFlipped((value) => !value)}
                        className="rounded-full border border-white/20 bg-white/10 hover:bg-white/20 px-3 py-1 text-xs inline-flex items-center gap-2"
                      >
                        <RotateCcw size={14} />
                        {flipped ? 'Show episode' : 'Show series'}
                      </button>
                    </div>

                    {!flipped ? (
                      <>
                        <h3 className="text-2xl md:text-4xl font-bold text-white leading-tight mb-4">
                          {currentEpisode.title}
                        </h3>
                        <p className="text-stone-200 leading-relaxed mb-6">
                          {currentEpisode.description}
                        </p>

                        <div className="mt-auto">
                          <div className="flex flex-wrap gap-2 mb-6">
                            {seriesThemes.map((theme) => (
                              <span
                                key={theme}
                                className="rounded-full bg-black/30 border border-white/15 px-3 py-1 text-xs text-stone-200"
                              >
                                {theme}
                              </span>
                            ))}
                          </div>

                          <div className="flex justify-center">
                            <Button
                              asChild
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              <a
                                href={currentEpisode.link}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Play className="mr-2 h-4 w-4" />
                                Listen on Spotify
                              </a>
                            </Button>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="flex flex-col h-full">
                        <p className="uppercase tracking-[0.25em] text-amber-300 text-xs mb-3">
                          Series journey
                        </p>
                        <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                          Step into the audio storyworld
                        </h3>
                        <p className="text-stone-200 leading-relaxed mb-6">
                          Move through the people and places behind the complete Sons of the Standard trilogy: Blood and Honour, Shadows of the Eagle, and Sons of the Standard—alongside Varro, Lucius, Aerwyna, Nestor, Julius, Cunobara, and the moral world they inhabit.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[260px] overflow-y-auto pr-1">
                          {romanBritainEpisodes.map((episode, index) => (
                            <button
                              type="button"
                              key={episode.title}
                              onClick={() => {
                                setEpisodeIndex(index);
                                setFlipped(false);
                              }}
                              className={`text-left rounded-xl border px-3 py-3 transition ${
                                index === episodeIndex
                                  ? 'border-amber-300 bg-amber-300/15'
                                  : 'border-white/15 bg-black/20 hover:bg-white/10'
                              }`}
                            >
                              <span className="block text-xs text-amber-200 mb-1">
                                {index + 1}. {episode.eyebrow}
                              </span>
                              <span className="block text-sm font-semibold text-white line-clamp-2">
                                {episode.title}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </article>

              <button
                type="button"
                onClick={goNextEpisode}
                className="hidden md:flex h-12 w-12 rounded-full border border-white/30 bg-black/40 hover:bg-amber-500 hover:text-black transition items-center justify-center"
                aria-label="Next episode"
              >
                <ArrowRight size={22} />
              </button>
            </div>
          </section>

          <section aria-label="Social media links" className="pb-12">
            <div className="flex items-center justify-center mb-8">
              <Users className="h-7 w-7 text-blue-400 mr-3" />
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                Connect on Social Media
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {socialMediaLinks.map((link) => (
                <article
                  key={link.title}
                  className="relative min-h-[320px] rounded-3xl overflow-hidden border border-white/15 bg-white/10 shadow-2xl group"
                >
                  <img
                    src={link.image}
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/55 to-black/20" />
                  <div className="relative h-full p-6 flex flex-col justify-end">
                    <p className="uppercase tracking-[0.25em] text-amber-200 text-xs mb-3">
                      {link.platform}
                    </p>
                    <h3 className="text-2xl font-bold text-white mb-3">{link.title}</h3>
                    <p className="text-stone-200 leading-relaxed mb-5">{link.description}</p>
                    <Button asChild className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                      <a href={link.url} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="mr-2 h-4 w-4" />
                        Visit Profile
                      </a>
                    </Button>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </div>
      </section>
    </main>
  );
}

export default Links;
